/**
 * useChatSession - 会话生命周期管理
 *
 * 职责：
 * 1. 开始新会话 (startSession)
 * 2. 开始预览会话 (startPreview)
 * 3. 从缓存恢复会话 (initializeFromCache)
 * 4. 重启会话 (restartSession)
 * 5. 结束会话 (endSession)
 *
 * 注意：该 Hook 内部调用 useEnginePlay, useMessageSync, useTransactions 等
 * 注意：engine 数据现在由 useEngine hook 自动从 API 获取并同步到 store
 */
import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetEnginePlayMysessionQueryKey,
  type HandlersStartEnginePlayRequestBody,
} from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'
import { showErrorData } from '@/lib/toast'
import { useTransactions } from '@/features/transactions'
import { type BatchAdapterInput } from '../lib/adapters'
import { useEngineStore } from '../stores/use-engine-store'
import type {
  EnginePlaySnapshotResponse,
  PreviewSessionType,
  PreviewResponse,
} from '../types/type'
import { useEnginePlay } from './use-engine-play'
import { useGalgameController } from './use-galgame-controller'
import { useMessageSync } from './use-message-sync'

interface UseChatSessionOptions {
  /**
   * 是否跳过初始的数据获取
   */
  skipInitialFetch?: boolean
}

export const useChatSession = (
  sessionId: string | undefined,
  options: UseChatSessionOptions = {}
) => {
  const { skipInitialFetch = false } = options
  const queryClient = useQueryClient()

  // ========== 依赖获取 ==========

  // 1. 获取 API 函数和数据
  const {
    startPlayAsync,
    startPreviewAsync,
    restartSessionAsync,
    endSessionAsync,
    messages: serverMessages,
    sessionInfo,
  } = useEnginePlay(sessionId, { skipInitialFetch })

  // 2. 获取消息同步函数
  const { syncMessagesToStore, createChoiceAction } = useMessageSync(sessionId)

  // 3. 其他依赖
  const { invalidateTransactions } = useTransactions()
  const resetPolling = useEngineStore((s) => s.choice.resetPolling)
  const setMessages = useEngineStore((s) => s.messages.setMessages)
  const { initSegments } = useGalgameController()

  // ========== 业务逻辑 ==========

  const invalidateSessionList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetEnginePlayMysessionQueryKey(),
    })
  }, [queryClient])

  /**
   * 开始新会话
   * 注意：engine 数据由 useEngine hook 在 Chat 页面自动获取并同步到 store
   */
  const startSession = useCallback(
    async (params: HandlersStartEnginePlayRequestBody) => {
      try {
        resetPolling()

        const res = await startPlayAsync({ data: params })
        const data = res.data

        if (data) {
          //const newSessionId = data.session?.id
          // if (newSessionId) {
          //   syncMessagesToStore(
          //     {
          //       session: data.session,
          //       currentScene:
          //         data.currentScene as BatchAdapterInput['currentScene'],
          //     },
          //     newSessionId
          //   )
          // }
          invalidateSessionList()
          invalidateTransactions()
          return data
        }
        return null
      } catch (e) {
        logger.error('开始会话失败', e)
        showErrorData('开启会话失败', e)
        return null
      }
    },
    [
      startPlayAsync,
      syncMessagesToStore,
      invalidateSessionList,
      resetPolling,
      invalidateTransactions,
    ]
  )

  /**
   * 开始预览
   * 注意：engine 数据由 useEngine hook 在 Chat 页面自动获取并同步到 store
   */
  const startPreview = useCallback(
    async (params: {
      engineId: string
      existingSessionId?: string
      sessionType?: PreviewSessionType
    }) => {
      try {
        resetPolling()

        if (params.existingSessionId) {
          return { sessionId: params.existingSessionId, isExisting: true }
        }

        const res = (await startPreviewAsync({
          data: {
            engineId: params.engineId,
            userInput: '',
            sessionType: params.sessionType || 'draft',
          },
        })) as unknown as PreviewResponse

        const data = res.data
        // if (data) {
        //   const previewMessage = adaptPreviewToUIMessage(
        //     {
        //       content: data.content,
        //       choices: data.choices || [],
        //     },
        //     createChoiceAction(data.sessionId)
        //   )

        //   setMessages([previewMessage])
        initSegments()
        invalidateSessionList()

        return { sessionId: data.sessionId, isExisting: false }
      } catch (e) {
        logger.error('开始预览失败', e)
        showErrorData('开启预览失败', e)
        return null
      }
    },
    [
      startPreviewAsync,
      setMessages,
      initSegments,
      createChoiceAction,
      invalidateSessionList,
      resetPolling,
    ]
  )

  /**
   * 从缓存恢复
   * 注意：不再此处重置轮询，以免在游玩过程中误伤正在进行的轮询
   */
  const initializeFromCache = useCallback(() => {
    if (serverMessages && sessionId) {
      const input: BatchAdapterInput = { storyHistory: serverMessages }
      if (sessionInfo) {
        input.session = sessionInfo as BatchAdapterInput['session']
      }
      syncMessagesToStore(input, sessionId)
    }
  }, [serverMessages, sessionInfo, sessionId, syncMessagesToStore])

  /**
   * 重启会话
   */
  const restartSession = useCallback(async () => {
    if (!sessionId) return null
    try {
      const res = (await restartSessionAsync({
        sessionId,
      })) as unknown as EnginePlaySnapshotResponse
      if (res?.data) {
        syncMessagesToStore(
          {
            session: res.data.session,
            currentScene: res.data.currentScene,
          },
          sessionId
        )
        return res.data
      }
      return null
    } catch (e) {
      logger.error('重开会话失败', e)
      return null
    }
  }, [restartSessionAsync, sessionId, syncMessagesToStore])

  /**
   * 结束会话
   */
  const endSession = useCallback(async () => {
    if (!sessionId) return
    try {
      await endSessionAsync({ data: { sessionId } })
    } catch (e) {
      logger.error('结束会话失败', e)
    }
  }, [endSessionAsync, sessionId])

  return useMemo(
    () => ({
      startSession,
      startPreview,
      initializeFromCache,
      restartSession,
      endSession,
      resetPolling, // 导出重置方法用于会话切换
      serverMessages, // 将这个传出去，方便 Chat 页面监听
      sessionInfo, // 新增：传出 sessionInfo 以便获取 engineId
    }),
    [
      startSession,
      startPreview,
      initializeFromCache,
      restartSession,
      endSession,
      resetPolling,
      serverMessages,
      sessionInfo,
    ]
  )
}
